import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import "./Scene3D.css";
import { skills } from "../../portfolio";
import { gsap, ScrollTrigger, prefersReducedMotion } from "../../utils/gsap";
import { getTheme } from "../../utils/theme";
import techIcons from "./techIcons";

const DARK_COLORS = ["#38BDF8", "#818CF8", "#C084FC", "#34D399"];
// deeper hues so the structure stays visible on a light background
const LIGHT_COLORS = ["#0369A1", "#4F46E5", "#9333EA", "#059669"];
const INTERACTIVE =
  "a,button,input,label,article,header,footer,.hero-panel,.cta,.contact-card,.proj-card,.cert-card,.deg-card,.experience-card";

const BADGE_PX = 128;

const luminance = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return 0.5;
  const n = parseInt(m[1], 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};

// Round badge texture: a disc with a coloured ring and the technology's logo.
function createBadge(tech) {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = BADGE_PX;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return { canvas, texture, img: null, tech, token: 0 };
}

function drawBadge(b, bg, ring) {
  const ctx = b.canvas.getContext("2d");
  const c = BADGE_PX / 2;
  ctx.clearRect(0, 0, BADGE_PX, BADGE_PX);
  ctx.beginPath();
  ctx.arc(c, c, c - 4, 0, Math.PI * 2);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = ring;
  ctx.stroke();
  if (b.img) {
    const size = BADGE_PX * 0.52;
    ctx.drawImage(b.img, c - size / 2, c - size / 2, size, size);
  }
  b.texture.needsUpdate = true;
}

// Re-skin a badge for the current theme (logo colour, disc, ring).
function skinBadge(b, light, ring) {
  const brand = (b.tech.style && b.tech.style.color) || "#94A3B8";
  const lum = luminance(brand);
  let logo = brand;
  let bg = light ? "#FFFFFF" : "#0E1524";
  if (lum < 0.3 && !light) logo = "#E2E8F0"; // near-black logo on dark disc
  if (lum > 0.7 && light) bg = "#0F172A"; // pale logo needs a dark disc
  drawBadge(b, bg, ring);
  const svg = techIcons[b.tech.fontAwesomeClassname];
  if (!svg) return;
  const token = ++b.token;
  const img = new Image();
  img.onload = () => {
    if (token !== b.token) return;
    b.img = img;
    drawBadge(b, bg, ring);
  };
  const sized = svg
    .replace("<svg", '<svg width="96" height="96"')
    .replace(/currentColor/g, logo);
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(sized);
}

const POINT_VERT = `
  uniform float uSize;
  uniform vec3 uMouse;
  uniform float uWave;
  uniform float uMorph;    // 0..3 : dust -> layers -> globe -> helix
  uniform float uScatter;  // 0..~1.6 : how far points fly from their shape
  attribute vec3 color;
  attribute vec3 aStack;
  attribute vec3 aGlobe;
  attribute vec3 aHelix;
  attribute vec3 aRand;
  attribute float aSeed;
  varying vec3 vColor;

  float ease(float t) { return t * t * (3.0 - 2.0 * t); }

  void main() {
    vColor = color;

    // morph between target shapes
    vec3 p;
    float m = clamp(uMorph, 0.0, 3.0);
    if (m < 1.0) p = mix(position, aStack, ease(m));
    else if (m < 2.0) p = mix(aStack, aGlobe, ease(m - 1.0));
    else p = mix(aGlobe, aHelix, ease(m - 2.0));

    // scatter: points burst outward on their own vector and swirl a little
    float a = uScatter * aSeed * 1.4;
    float cs = cos(a);
    float sn = sin(a);
    p.xz = mat2(cs, -sn, sn, cs) * p.xz;
    p += aRand * uScatter * (2.2 + aSeed * 3.6);

    vec4 wp = modelMatrix * vec4(p, 1.0);

    // pointer repel
    vec3 d = wp.xyz - uMouse;
    float dist = length(d.xy);
    float f = smoothstep(2.6, 0.0, dist);
    wp.xyz += normalize(vec3(d.xy, 0.0001)) * f * 1.1;

    // click shock-wave travelling outwards from the centre
    float r = length(p);
    float w = exp(-pow((r - uWave) * 1.3, 2.0));
    wp.xyz += normalize(p + vec3(0.0001)) * w * 0.9;

    vec4 mv = viewMatrix * wp;
    float formed = 1.0 + min(uMorph, 1.0) * 0.8; // shapes read better when dense
    gl_PointSize = uSize * formed * (9.0 / -mv.z) * (1.0 + f * 1.6 + w * 1.4 + uScatter * 0.25);
    gl_Position = projectionMatrix * mv;
  }
`;
const POINT_FRAG = `
  varying vec3 vColor;
  uniform float uOpacity;
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    gl_FragColor = vec4(vColor, smoothstep(0.5, 0.0, d) * uOpacity);
  }
`;

// Fixed WebGL backdrop: a "stack" built from the skills data. One hex layer per
// skill group, one node per technology, data packets travelling the spine.
// Scroll (GSAP ScrollTrigger) moves the camera; the pointer hovers, repels,
// tilts and click-pulses the structure.
export default function Scene3D() {
  const mountRef = useRef(null);
  const tipRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
      });
    } catch (e) {
      return undefined; // no WebGL: the page still works without the backdrop
    }

    const reduce = prefersReducedMotion();
    const small = window.innerWidth < 768;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    renderer.setPixelRatio(dpr);
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      55,
      window.innerWidth / window.innerHeight,
      0.1,
      100
    );
    camera.position.set(0, 0, 9);

    const group = new THREE.Group(); // scroll-driven
    const tilt = new THREE.Group(); // pointer-driven
    group.add(tilt);
    scene.add(group);

    const disposables = [];
    const track = (o) => {
      disposables.push(o);
      return o;
    };

    // --- dust field (shader points: pointer repel + click wave)
    const count = small ? 800 : 2000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const mix = new Float32Array(count);
    const tmp = new THREE.Color();
    for (let i = 0; i < count; i++) {
      const r = 4 + Math.random() * 8;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.8;
      positions[i * 3 + 2] = r * Math.cos(phi);
      mix[i] = Math.random();
    }
    // target shapes the dust morphs through while scrolling
    const stackPts = new Float32Array(count * 3);
    const globePts = new Float32Array(count * 3);
    const helixPts = new Float32Array(count * 3);
    const rands = new Float32Array(count * 3);
    const seeds = new Float32Array(count);
    const nLayers = Math.max(skills.data.length, 1);
    const layerGap = small ? 1.5 : 1.9;
    const HEX_R = 3.3;
    const inHex = (x, z) => {
      // regular hexagon, circumradius HEX_R
      const k = HEX_R * 0.866;
      const ax = Math.abs(x);
      const az = Math.abs(z);
      return az <= k && 0.866 * ax + 0.5 * az <= k;
    };
    for (let i = 0; i < count; i++) {
      // stacked hex discs, one per skill group (mirrors the real layers)
      let x, z;
      do {
        x = (Math.random() * 2 - 1) * HEX_R;
        z = (Math.random() * 2 - 1) * HEX_R;
      } while (!inHex(x, z));
      const layer = i % nLayers;
      stackPts.set(
        [x, ((nLayers - 1) / 2 - layer) * layerGap + (Math.random() - 0.5) * 0.12, z],
        i * 3
      );

      // globe
      const gt = Math.random() * Math.PI * 2;
      const gp = Math.acos(2 * Math.random() - 1);
      const gr = 3.6;
      globePts.set(
        [
          gr * Math.sin(gp) * Math.cos(gt),
          gr * Math.cos(gp),
          gr * Math.sin(gp) * Math.sin(gt),
        ],
        i * 3
      );

      // double helix
      const strand = i % 2;
      const u = Math.random();
      const ang = u * Math.PI * 6 + strand * Math.PI;
      helixPts.set(
        [
          Math.cos(ang) * 1.7 + (Math.random() - 0.5) * 0.15,
          (u - 0.5) * 7.5,
          Math.sin(ang) * 1.7 + (Math.random() - 0.5) * 0.15,
        ],
        i * 3
      );

      // random scatter direction
      const rt = Math.random() * Math.PI * 2;
      const rp = Math.acos(2 * Math.random() - 1);
      rands.set(
        [
          Math.sin(rp) * Math.cos(rt),
          Math.sin(rp) * Math.sin(rt),
          Math.cos(rp),
        ],
        i * 3
      );
      seeds[i] = Math.random();
    }

    const pointsGeo = track(new THREE.BufferGeometry());
    pointsGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    pointsGeo.setAttribute("aStack", new THREE.BufferAttribute(stackPts, 3));
    pointsGeo.setAttribute("aGlobe", new THREE.BufferAttribute(globePts, 3));
    pointsGeo.setAttribute("aHelix", new THREE.BufferAttribute(helixPts, 3));
    pointsGeo.setAttribute("aRand", new THREE.BufferAttribute(rands, 3));
    pointsGeo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
    pointsGeo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const uniforms = {
      uSize: { value: (small ? 3 : 2.4) * dpr },
      uMouse: { value: new THREE.Vector3(999, 999, 0) },
      uWave: { value: -10 },
      uMorph: { value: 0 },
      uScatter: { value: 0 },
      uOpacity: { value: 0.85 },
    };
    const pointsMat = track(
      new THREE.ShaderMaterial({
        uniforms,
        vertexShader: POINT_VERT,
        fragmentShader: POINT_FRAG,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    const points = new THREE.Points(pointsGeo, pointsMat);
    points.frustumCulled = false;
    group.add(points);

    // --- the stack: one layer per skill group
    const groups = skills.data;
    const spacing = small ? 1.5 : 1.9;
    const hexGeo = track(new THREE.CylinderGeometry(2.7, 2.7, 0.1, 6));
    const edgeGeo = track(new THREE.EdgesGeometry(hexGeo));
    const layers = [];
    const nodes = [];
    const nodeSize = small ? 0.32 : 0.36;
    const layerRefs = [];

    groups.forEach((g, gi) => {
      const color = new THREE.Color(DARK_COLORS[gi % DARK_COLORS.length]);
      const layer = new THREE.Group();
      layer.position.y = ((groups.length - 1) / 2 - gi) * spacing;

      const fillMat = track(
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.07,
          depthWrite: false,
        })
      );
      layer.add(new THREE.Mesh(hexGeo, fillMat));
      const layerNodes = [];
      const edgeMat = track(
        new THREE.LineBasicMaterial({
          color,
          transparent: true,
          opacity: 0.7,
        })
      );
      layer.add(new THREE.LineSegments(edgeGeo, edgeMat));

      // technology nodes ring the layer, tied back to the spine
      const techs = g.softwareSkills;
      const linePts = [];
      techs.forEach((t, ti) => {
        const a = (ti / techs.length) * Math.PI * 2;
        const radius = 2.05;
        const x = Math.cos(a) * radius;
        const z = Math.sin(a) * radius;
        const y = 0.12 + (ti % 2) * 0.18;
        const badge = createBadge(t);
        const mat = track(
          new THREE.SpriteMaterial({
            map: badge.texture,
            transparent: true,
            depthWrite: false,
          })
        );
        track(badge.texture);
        const node = new THREE.Sprite(mat);
        node.scale.set(nodeSize, nodeSize, 1);
        node.position.set(x, y, z);
        node.userData = {
          name: t.skillName,
          group: g.title,
          badge,
          size: nodeSize,
        };
        layer.add(node);
        nodes.push(node);
        layerNodes.push(node);
        linePts.push(0, 0, 0, x, y, z);
      });
      const spokeGeo = track(new THREE.BufferGeometry());
      spokeGeo.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(linePts, 3)
      );
      const spokeMat = track(
        new THREE.LineBasicMaterial({
          color,
          transparent: true,
          opacity: 0.18,
        })
      );
      layerRefs.push({ fillMat, edgeMat, spokeMat, nodes: layerNodes });
      layer.add(new THREE.LineSegments(spokeGeo, spokeMat));

      layer.userData = { dir: gi % 2 === 0 ? 1 : -1 };
      tilt.add(layer);
      layers.push(layer);
    });

    // spine + data packets
    const topY = layers[0] ? layers[0].position.y : 0;
    const botY = layers.length ? layers[layers.length - 1].position.y : 0;
    const spineGeo = track(new THREE.BufferGeometry());
    spineGeo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute([0, topY, 0, 0, botY, 0], 3)
    );
    const spineMat = track(
      new THREE.LineBasicMaterial({
        color: "#F1F5F9",
        transparent: true,
        opacity: 0.35,
      })
    );
    tilt.add(new THREE.Line(spineGeo, spineMat));

    // packets are logo badges too, drawn from technologies across all groups
    const allTechs = groups.flatMap((g) => g.softwareSkills);
    const packetSize = small ? 0.21 : 0.24;
    const packets = Array.from({ length: 6 }, (_, i) => {
      const tech = allTechs[Math.floor((i / 6) * allTechs.length)];
      const badge = createBadge(tech);
      track(badge.texture);
      const mat = track(
        new THREE.SpriteMaterial({
          map: badge.texture,
          transparent: true,
          depthWrite: false,
        })
      );
      const m = new THREE.Sprite(mat);
      m.scale.set(packetSize, packetSize, 1);
      m.userData = { phase: i / 6, dir: i % 2 ? -1 : 1, badge };
      tilt.add(m);
      return m;
    });

    // --- interaction state
    const state = {
      target: 0,
      current: 0,
      speed: 0,
      mx: 0,
      my: 0,
      boost: 0,
      overUI: false,
      hovered: null,
      light: false,
    };
    const ndc = new THREE.Vector2(9, 9);

    // Recolour for light/dark: additive glow vanishes on white, so light mode
    // uses normal blending and deeper hues.
    const paint = (mode) => {
      const light = mode === "light";
      state.light = light;
      const pal = light ? LIGHT_COLORS : DARK_COLORS;
      pointsMat.blending = light ? THREE.NormalBlending : THREE.AdditiveBlending;
      pointsMat.needsUpdate = true;
      uniforms.uOpacity.value = light ? 0.75 : 0.85;
      const a = new THREE.Color(pal[0]);
      const b = new THREE.Color(pal[1]);
      for (let i = 0; i < count; i++) {
        tmp.copy(a).lerp(b, mix[i]);
        colors.set([tmp.r, tmp.g, tmp.b], i * 3);
      }
      pointsGeo.attributes.color.needsUpdate = true;
      layerRefs.forEach((r, gi) => {
        const c = new THREE.Color(pal[gi % pal.length]);
        r.fillMat.color.copy(c);
        r.edgeMat.color.copy(c);
        r.spokeMat.color.copy(c);
        r.fillMat.opacity = light ? 0.12 : 0.07;
        r.spokeMat.opacity = light ? 0.35 : 0.18;
        r.nodes.forEach((n) => {
          skinBadge(n.userData.badge, light, `#${c.getHexString()}`);
        });
      });
      const ink = light ? "#0F172A" : "#F1F5F9";
      spineMat.color.set(ink);
      packets.forEach((m) => skinBadge(m.userData.badge, light, ink));
      spineMat.opacity = light ? 0.45 : 0.35;
    };
    paint(getTheme());
    const onTheme = (e) => {
      paint(e.detail);
      if (reduce) renderer.render(scene, camera);
    };
    window.addEventListener("themechange", onTheme);
    const raycaster = new THREE.Raycaster();
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const hit = new THREE.Vector3();
    const tip = tipRef.current;

    const trigger = ScrollTrigger.create({
      trigger: document.documentElement,
      start: 0,
      end: "max",
      onUpdate: (self) => {
        state.target = self.progress;
        state.speed = Math.min(Math.abs(self.getVelocity()) / 2500, 1);
      },
    });

    const onPointer = (e) => {
      state.mx = (e.clientX / window.innerWidth - 0.5) * 2;
      state.my = (e.clientY / window.innerHeight - 0.5) * 2;
      state.overUI = !!(e.target.closest && e.target.closest(INTERACTIVE));
      ndc.set(state.mx, -state.my);
      if (tip) {
        tip.style.transform = `translate(${e.clientX + 16}px, ${e.clientY + 16}px)`;
      }
    };
    window.addEventListener("pointermove", onPointer, { passive: true });

    const burst = { v: 0 };
    const pulse = () => {
      gsap.fromTo(
        burst,
        { v: 1.4 },
        { v: 0, duration: 1.8, ease: "power3.out", overwrite: true }
      );
      uniforms.uWave.value = 0;
      gsap.to(uniforms.uWave, { value: 16, duration: 1.8, ease: "power2.out" });
      layers.forEach((l, i) => {
        gsap.fromTo(
          l.scale,
          { x: 1, y: 1, z: 1 },
          {
            x: 1.14,
            y: 1.14,
            z: 1.14,
            duration: 0.25,
            yoyo: true,
            repeat: 1,
            delay: i * 0.12,
            ease: "power2.out",
          }
        );
      });
      state.boost = 1;
    };
    const onClick = (e) => {
      if (e.target.closest && e.target.closest(INTERACTIVE)) return;
      pulse();
    };
    window.addEventListener("click", onClick);

    const render = () => renderer.render(scene, camera);

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      render();
    };
    window.addEventListener("resize", onResize);

    // Page height changes with route / content; keep triggers accurate.
    let refreshTimer = 0;
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            clearTimeout(refreshTimer);
            refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 150);
          })
        : null;
    if (ro) ro.observe(document.body);
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onLoad);

    const setHover = (node) => {
      if (node === state.hovered) return;
      const prev = state.hovered;
      if (prev) {
        const z = prev.userData.size;
        gsap.to(prev.scale, { x: z, y: z, duration: 0.3, overwrite: true });
      }
      state.hovered = node;
      if (node) {
        const z = node.userData.size * 1.8;
        gsap.to(node.scale, {
          x: z,
          y: z,
          duration: 0.35,
          ease: "back.out(2)",
          overwrite: true,
        });
        if (tip) {
          tip.innerHTML = `<strong>${node.userData.name}</strong><span>${node.userData.group}</span>`;
          tip.classList.add("on");
        }
      } else if (tip) {
        tip.classList.remove("on");
      }
    };

    let tick = null;
    if (reduce) {
      render();
    } else {
      let t = 0;
      tick = (time, delta) => {
        t += delta / 1000;
        state.current += (state.target - state.current) * 0.06;
        state.speed *= 0.94;
        state.boost *= 0.97;

        // Scroll morph: each third of the page holds one shape, then the
        // points scatter and re-form as the next one. Fast scrolling and clicks
        // add extra scatter on top.
        const m = Math.min(state.current * 3, 3);
        const seg = Math.min(Math.floor(m), 2);
        const tr = Math.min(Math.max((m - seg - 0.4) / 0.6, 0), 1);
        uniforms.uMorph.value = seg + tr;
        uniforms.uScatter.value = Math.min(
          1.6,
          Math.sin(Math.PI * tr) * 0.9 + state.speed * 0.9 + burst.v
        );
        const p = state.current;

        // scroll: composition sweeps sideways, turns and dollies in
        const side = small ? 0 : Math.cos(p * Math.PI * 2) * 2.6;
        group.position.x += (side - group.position.x) * 0.05;
        group.rotation.y = t * 0.04 + p * Math.PI * 1.5;
        points.rotation.y += 0.0007 + state.speed * 0.02;

        // pointer: the whole stack leans toward the cursor
        tilt.rotation.x += (0.35 + state.my * 0.3 - tilt.rotation.x) * 0.05;
        tilt.rotation.z += (-state.mx * 0.12 - tilt.rotation.z) * 0.05;

        // layers counter-rotate; pointer distance from centre adds spin
        const spin = 0.003 + Math.abs(state.mx) * 0.006 + state.speed * 0.03;
        layers.forEach((l) => {
          l.rotation.y += l.userData.dir * spin;
        });

        // data packets climb/descend the spine, faster after a click
        packets.forEach((m) => {
          m.userData.phase =
            (m.userData.phase + (0.0025 + state.boost * 0.02) * m.userData.dir + 1) % 1;
          m.position.y = botY + (topY - botY) * m.userData.phase;
          const pz = packetSize * (1 + Math.sin(t * 6 + m.userData.phase * 9) * 0.12);
          m.scale.set(pz, pz, 1);
        });

        // dolly + parallax
        const camZ = 9 - p * 2.8;
        camera.position.z += (camZ - camera.position.z) * 0.08;
        camera.position.x += (state.mx * 0.6 - camera.position.x) * 0.04;
        camera.position.y += (-state.my * 0.4 - camera.position.y) * 0.04;
        camera.lookAt(0, 0, 0);

        // pointer -> world plane (shader repel) and node hover
        raycaster.setFromCamera(ndc, camera);
        if (raycaster.ray.intersectPlane(plane, hit)) uniforms.uMouse.value.copy(hit);
        if (!state.overUI) {
          const hits = raycaster.intersectObjects(nodes, false);
          setHover(hits.length ? hits[0].object : null);
        } else {
          setHover(null);
        }

        render();
      };
      gsap.ticker.add(tick);
    }

    return () => {
      if (tick) gsap.ticker.remove(tick);
      trigger.kill();
      gsap.killTweensOf(uniforms.uWave);
      gsap.killTweensOf(burst);
      nodes.forEach((n) => gsap.killTweensOf(n.scale));
      layers.forEach((l) => gsap.killTweensOf(l.scale));
      clearTimeout(refreshTimer);
      if (ro) ro.disconnect();
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("click", onClick);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onLoad);
      window.removeEventListener("themechange", onTheme);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <>
      <div className="scene3d" ref={mountRef} aria-hidden="true" />
      <div className="scene-tip" ref={tipRef} aria-hidden="true" />
    </>
  );
}
